import _ from 'lodash'

/**
 * Information about different types of files, their extensions, and the MIME
 * strings the browser understands.  Should be kept in sync with FileInfo.kt.
 */
var fileInfo = {

	/**
	 * Hash of file type to value for "accept" attribute of <input type="file">.
	 * Safari wants a mime type, and other browsers want a file extension.
	 * Extensions are added programmatically later on.  I built these from our
	 * mime.types file and these resources:
	 *
	 * https://www.sitepoint.com/mime-types-complete-list/
	 * https://stackoverflow.com/questions/1454777/apple-iwork-mime-types
	 * https://filext.com/file-extension/7Z
	 *
	 * In the source here you only see mime types, but the extensions are also
	 * added near the end of this file.
	 */
	accepts: {
		// #24009 tiff files can't be recognized by Chrome, so don't count them as images
		image: 'image/gif,image/jpeg,image/x-xwindowdump,image/png,image/bmp,image/svg+xml',
		// #59845 logo image types exclude bmp, svg, and xwd(#59910)
		logoImage: 'image/gif,image/jpeg,image/png',
		document: 'application/rtf,application/x-rtf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/x-iwork-pages-sffpages',
		audio: 'audio/basic,audio/midi,audio/x-mpeg',
		video: 'video/mpeg,video/quicktime,video/x-msvideo',
		spreadsheet: 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/x-iwork-numbers-sffnumbers,application/vnd.google-apps.spreadsheet',
		// you can upload a Numbers file to the documents section, but we can't
		// read Numbers files, so in places where the user uploads a spreadsheet
		// we want to exclude Numbers
		spreadsheetWithoutNumbers: 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
		// #29607 Allow uploading zip files for datasets
		spreadsheetOrZip: 'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/zip',
		// CSV files only
		csv: 'text/csv',
		presentation: 'application/x-iwork-keynote-sffkey,application/mspowerpoint,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
		compressed: 'application/zip,application/x-7z-compressed',
		acrobat: 'application/pdf',
		visio: 'application/vnd.visio,application/vnd.ms-visio.drawing,application/vnd.ms-visio.drawing.macroEnabled.12,application/vnd.visio.template,application/vnd.ms-visio.template.main+xml,application/vnd.ms-visio.template.macroEnabled.12,application/vnd.visio.stencil,application/vnd.ms-visio.stencil.main+xml,application/vnd.ms-visio.stencil.macroEnabled.12,application/vnd.visio.webdrawing,application/vnd.ms-visio.viewer,application/vnd.ms-visio.drawing,application/vnd.visio.stencil+xml,application/vnd.visio.template+xml,application/vnd.visio',
		text: 'text/plain'
	},

	/**
	 * A hash of the different file types to the extensions recognized for
	 * each type.  #8337 describes the types of icons we want to have available
	 */
	extensions: {
		// #59845 logo image types exclude bmp, svg, and xwd(#59910)
		// #60158 this must be before image or we'll get the wrong icon
		logoImage:  [ 'gif', 'jpeg', 'jpg', 'jpe', 'png'],
		// #24009 tiff files can't be recognized by Chrome, so don't count them as images
		image: [ 'gif', 'jpeg', 'jpg', 'jpe', 'xwd', 'png', 'bmp', 'svg' ],
		document: [ 'rtf', 'doc', 'docx', 'pages' ],
		audio: [ 'au', 'midi', 'mid', 'mpa' ],
		video: [ 'mpeg', 'mpg', 'mpe', 'qt', 'mov', 'avi' ],
		// you can upload a Numbers file to the documents section, but we can't
		// read Numbers files, so in places where the user uploads a spreadsheet
		// we want to exclude Numbers. Put this BEFORE spreadsheet or
		// file-info-test fails
		spreadsheetWithoutNumbers: [ 'csv', 'xls', 'xlsx' ],
		// #29607 Allow uploading zip files for datasets
		spreadsheetOrZip: [ 'csv', 'xls', 'xlsx', 'zip' ],
		// CSV files only
		csv: [ 'csv' ],
		excel: [ 'xls', 'xlsx' ],
		spreadsheet: [ 'xls', 'xlsx', 'numbers', 'csv' ],
		presentation: [ 'keynote', 'ppt', 'pptx' ],
		compressed: [ 'zip', '7zip', '7z' ],
		acrobat: [ 'pdf' ],
		visio: [ 'vsd', 'vsdx', 'vsdm', 'vst', 'vstx', 'vstm', 'vss', 'vssx', 'vssm', 'vsw', 'vdw', 'vdx', 'vsx', 'vtx', 'vsl' ],
		text: [ 'text', 'txt' ]
	},

	/**
	 * Needed for files that don't have an extension
	 */
	mimeTypes: {
		'application/vnd.google-apps.spreadsheet': 'spreadsheet'
	},

	// build a hash of extension to type to speed things up
	types: _.memoize(function() {
		var types = {}
		_.forEach(this.extensions, function(array, type) {
			_.forEach(array, function(extension) {
				types[extension] = type
			})
		})
		return types
	}),

	/**
	 * @returns {string|null} The filename without the extension or any
	 * preceding path
	 */
	fileName: function(str) {
		if (!str) {
			return null
		}

		// remove the extension
		var index = str.lastIndexOf('.')
		if (index != -1) {
			str = str.substring(0, index)
		}

		// fix paths with / characters
		index = str.lastIndexOf('/')
		if (index != -1) {
			str = str.substring(index + 1, str.length)
		}

		// fix paths with \ characters
		index = str.lastIndexOf('\\')
		if (index != -1) {
			str = str.substring(index + 1, str.length)
		}

		return str
	},

	/**
	 * Returns the extension part of the given filename.  If the passed in
	 * string is already just an extension, it is returned unchanged
	 */
	extension: function(str) {
		if (!str) {
			return null
		}

		var index = str.lastIndexOf('.')
		if (index != -1) {
			return str.substring(index + 1, str.length).toLowerCase()
		}
		// if we reached here the the passed in str was already just an
		// extension
		return str.toLowerCase()
	},

	/**
	 * Returns the type for a filename or extension, or 'unknown' if it is not
	 * known.  If an undefined or empty string is passed in, returns null
	 */
	type: function(str) {
		if (_.isUndefined(str) || _.isEmpty(str)) {
			return null
		}

		return this.types()[this.extension(str)] || 'unknown'
	},

	/**
	 * Returns the icon for a filename or extension, or 'unknown' if it is not
	 * known.  If an undefined or empty string is passed in, returns null
	 */
	icon: function(str) {
		return this._iconForType(this.type(str))
	},

	iconForMimeType: function(mimeType) {
		if (_.isUndefined(mimeType) || _.isEmpty(mimeType)) {
			return null
		}
		return this._iconForType(this.mimeTypes[mimeType] || 'document-blank')
	},

	_iconForType(type) {
		if (type == null) {
			return null
		}
		if (type == 'visio') {
			return 'docs-document'
		}
		return type === 'unknown' ? 'document-blank' : 'docs-' + type
	}

}

_.each(fileInfo.extensions, (extensions, type) => {
	let extensionStr = _(extensions)
		.map(extension => '.' + extension)
		.join()
	fileInfo.accepts[type] += ',' + extensionStr
})

export default fileInfo

fileInfo.__filename__ = 'components/files/file-info.js'
